import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFileExplorerContext } from './useFileExplorerContext';
import { getFiles, TreeFolder } from '../components/FileExplorer/services';
import { PathNode } from '../components/FileExplorer/Context/FileExplorerContext';

export function useFileExplorerFolder({
  id, name, parentPath = [], permissions, description, remove_file_prefix, shareable_link,
}: TreeFolder & { parentPath?: PathNode[] }) {
  const { setActiveFolder, activeFolder } = useFileExplorerContext();
  const [isExpanded, setIsExpanded] = useState(false);

  // Is required to declare isActive here since we need this
  // state in the useQuery hook below
  const isActive = activeFolder?.id === id;
  const {
    data: folderData, isLoading, isError, isInitialLoading, error, isFetching, status, isRefetching,
  } = useQuery({
    queryKey: [`filetree-folder-${id}`],
    queryFn: () => getFiles({ id }),
    // Only fetch when expanded to avoid unnecessary API calls
    enabled: isExpanded || isActive,
  });

  const isFetched = status === 'success';
  // Check if folder has children to determine if collapse arrow should be shown
  const shouldExpand = useMemo(() => (folderData?.data.folders?.length ?? 0) > 0, [folderData]);

  const handleToggle = () => {
    const newPath = [...parentPath, { id, name }];

    // Always set as active to prevent double-click behavior and provide immediate feedback
    const newActiveFolder = {
      id,
      name,
      path: newPath,
      content: {
        files: folderData?.data.files ?? [],
        folders: folderData?.data.folders ?? [],
      },
      permissions,
      shareable_link,
      // Show loading only on first expansion to maintain responsive UI
      isLoading: !isFetched && !isExpanded,
      // Track fetching state for potential future use while keeping optimistic approach
      isFetching: (!isFetched && !isExpanded) || isFetching,
      description,
      remove_file_prefix,
    };

    setActiveFolder(newActiveFolder);
    setIsExpanded((prev) => !prev);
  };

  // Update activeFolder when data changes to keep UI in sync with server state
  useEffect(() => {
    if (isActive && activeFolder && folderData) {
      const newContent = {
        files: folderData.data.files ?? [],
        folders: folderData.data.folders ?? [],
      };

      // Update with fresh data and reset loading states
      setActiveFolder((prev) => ({
        ...prev!,
        content: newContent,
        isLoading: false,
        // Keep tracking refetch state for consistency
        isFetching: isRefetching,
        permissions,
        description,
        remove_file_prefix,
        shareable_link,
      }));
    }
  }, [
    isActive,
    isFetched,
    isRefetching,
    folderData?.data?.files,
    folderData?.data?.folders,
  ]);

  // Generate unique collapse ID to avoid conflicts in Bootstrap collapse functionality
  const collapseId = `folder-collapse-${name.replace(/\s+/g, '-')}-filetree-folder-${id}`;
  const attributes = {
    'data-bs-toggle': 'collapse',
    'data-bs-target': `#${collapseId}`,
    'data-active': isActive,
    'data-kind': 'folder',
    // Use isInitialLoading for CSS-based loading indicators
    'data-loading': isInitialLoading,
  };

  return {
    collapseId,
    isExpanded,
    setIsExpanded,
    handleToggle,
    folders: folderData?.data.folders,
    files: folderData?.data.files,
    isLoading,
    isFetched,
    isError,
    isInitialLoading,
    isFetching,
    error,
    isActive,
    shouldExpand,
    attributes,
  };
}
